import React, { useEffect, useMemo, useState } from "react";
import {
  Typography,
  Box,
  CircularProgress,
  Button,
  Modal,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  SelectChangeEvent,
  Snackbar,
  Alert,
  IconButton,
  debounce,
} from "@mui/material";
import axios from "axios";
import to from "await-to-js";
import CustomTable from "../components/CustomTable";
import { useNavigate } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import FroalaEditor from "react-froala-wysiwyg";
import "froala-editor/css/froala_editor.pkgd.min.css";
import FroalaEditorComponent from "../components/Editor";
import { axiosWithAuth } from "../plugins/axios";
import {
  DataGrid,
  GridColDef,
  GridColumnMenuContainer,
  GridColumnMenuProps,
  GridColumnMenuSortItem,
  GridColumnMenuHideItem,
  GridSortModel,
  GridColumnMenuManageItem,
  getGridStringOperators,
  GridFilterModel,
} from "@mui/x-data-grid";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 900,
  height: "80vh",
  overflowY: "scroll",
  bgcolor: "background.paper",
  p: 4,
};

interface Cohort {
  _id: number;
  cohortName: string;
}

interface Category {
  id: string;
  name: string;
}

function CustomColumnMenu(props: GridColumnMenuProps) {
  const { hideMenu, colDef, open } = props;
  return (
    <GridColumnMenuContainer hideMenu={hideMenu} colDef={colDef} open={open}>
      <GridColumnMenuSortItem colDef={colDef} onClick={hideMenu} />
      <GridColumnMenuHideItem colDef={colDef} onClick={hideMenu} />
      <GridColumnMenuManageItem colDef={colDef} onClick={hideMenu} />
    </GridColumnMenuContainer>
  );
}
function Cohorts() {
  const [cohorts, setCohorts] = useState<Cohort[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [open, setOpen] = useState(false);
  const [cloneOpen, setCloneOpen] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [formData, setFormData] = useState({
    cohortId: "",
    quizId: "",
    problemStatement: "",
    categoryId: "",
    teachUrl: "",
  });
  const [formErrors, setFormErrors] = useState({
    quizId: false,
    problemStatement: false,
    categoryId: false,
  });

  const [cloneFormData, setCloneFormData] = useState({
    cloneFromCohortId: "",
    cloneFromQuizId: "",
    cloneToCohortId: "",
    cloneToQuizId: "",
    teachUrl: "",
  });

  const [cloneFormErrors, setCloneFormErrors] = useState({
    cloneFromCohortId: false,
    cloneFromQuizId: false,
    cloneToCohortId: false,
    cloneToQuizId: false,
    teachUrl: false,
  });
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>();

  const apiUrl = process.env.REACT_APP_PIPELINE_API;
  const navigate = useNavigate();

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setFormErrors({
      quizId: false,
      problemStatement: false,
      categoryId: false,
    });
  };

  const handleCloneOpen = () => setCloneOpen(true);
  const handleCloneClose = () => {
    setCloneOpen(false);
    setCloneFormErrors({
      cloneFromCohortId: false,
      cloneFromQuizId: false,
      cloneToCohortId: false,
      cloneToQuizId: false,
      teachUrl: false,
    });
  };

  const handleToastClose = () => setToastOpen(false);

  const debounceFetchCohorts = useMemo(
    () =>
      debounce((filter) => {
        setFilterModel(filter);
      }, 1000),
    [filterModel]
  );

  const fetchCohorts = async () => {
    const [err, response] = await to(
      axiosWithAuth(
        `${apiUrl}cohortconfiguration/cohorts`,
        {
          params: {
            page: page + 1,
            limit: rowsPerPage,
            sortColumn: sortModel[0]?.field || null,
            sortDirection: sortModel[0]?.sort || null,
            filterColumn: filterModel?.items[0]?.field || null,
            filterValue: filterModel?.items[0]?.value || null,
            filterOperator: filterModel?.items[0]?.operator || null,
          },
        },
        "get"
      )
    );

    if (err) {
      setError("Failed to load cohorts");
    } else {
      const { data, totalCount } = response.data;
      setCohorts(data);
      setTotalCount(totalCount);
    }

    setLoading(false);
  };

  useEffect(() => {
    return () => {
      debounceFetchCohorts.clear();
    };
  }, [debounceFetchCohorts]);

  const fetchCategories = async () => {
    const [err, response] = await to(
      axiosWithAuth(`${apiUrl}cohortconfiguration/submissiontypes`, {}, "get")
    );

    if (err) {
      setError("Failed to load categories");
    } else {
      const categories = response.data?.data.map((category: any) => ({
        id: category._id,
        name: category.name,
        categoryId: category.categoryId,
      }));
      setCategories(categories);
    }
  };

  useEffect(() => {
    fetchCohorts();
    fetchCategories();
  }, [apiUrl, page, rowsPerPage, sortModel, filterModel]);

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name as string]: value }));
    setFormErrors((prev) => ({ ...prev, [name as string]: false }));
  };

  const handleCloneFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCloneFormData((prev) => ({ ...prev, [name as string]: value }));
    setCloneFormErrors((prev) => ({ ...prev, [name as string]: false }));
  };

  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    setFormData((prev) => ({
      ...prev,
      categoryId: event.target.value as string,
    }));
    setFormErrors((prev) => ({ ...prev, categoryId: false }));
  };

  const handleFroalaChange = (content: string) => {
    setFormData((prev) => ({ ...prev, problemStatement: content }));
    setFormErrors((prev) => ({ ...prev, problemStatement: false }));
  };

  const handleFormSubmit = async () => {
    const { cohortId, quizId, problemStatement, categoryId, teachUrl } =
      formData;
    console.log(categoryId, "categoryId");
    const errors = {
      quizId: quizId.trim() === "",
      problemStatement: problemStatement.trim() === "",
      categoryId: categoryId === ""
    };

    setFormErrors(errors);

    console.log(errors, "errors");

    if (Object.values(errors).some((error) => error)) return;

    setIsSubmitting(true); // Set submitting state to true

    const [submitErr] = (await to(
      axiosWithAuth(
        `${apiUrl}cohortconfiguration/create`,
        {
          ...formData,
          submissionType: Number(categoryId),
          settings: { submissionType: Number(categoryId) },
        },
        "post"
      )
    )) as any;

    if (submitErr) {
      const { data: { message = null } = {} } = submitErr.response;
      setToastMessage(message);
      setToastOpen(true);
    } else {
      handleClose();
      setFormData({
        cohortId: "",
        quizId: "",
        problemStatement: "",
        categoryId: "",
        teachUrl: "",
      });
      setFormErrors({
        quizId: false,
        problemStatement: false,
        categoryId: false,
      });
      setLoading(true);

      const [fetchErr, response] = await to(
        axiosWithAuth(
          `${apiUrl}cohortconfiguration/cohorts`,
          {
            params: { page: page + 1, limit: rowsPerPage },
          },
          "get"
        )
      );

      if (!fetchErr) {
        setCohorts(response.data.data);
      }
    }

    setIsSubmitting(false); // Reset submitting state
    setLoading(false);
  };
  const handleCloneFormSubmit = async () => {
    const { cloneFromCohortId, cloneFromQuizId, cloneToCohortId, cloneToQuizId, teachUrl } = cloneFormData;

    // Validate the fields
    const errors = {
      cloneFromCohortId: cloneFromCohortId.trim() === "",
      cloneFromQuizId: cloneFromQuizId.trim() === "",
      cloneToCohortId: cloneToCohortId.trim() === "",
      cloneToQuizId: cloneToQuizId.trim() === "",
      teachUrl: teachUrl.trim() === "",
    };

    setCloneFormErrors(errors);

    // If there are any validation errors, stop form submission
    if (Object.values(errors).some((error) => error)) return;

    setIsSubmitting(true); // Set submitting state to true

    const requestData = {
      cloneFrom: {
        cohortId: cloneFromCohortId,
        quizId: cloneFromQuizId,
      },
      cloneTo: {
        cohortId: cloneToCohortId,
        quizId: cloneToQuizId,
      },
      teachUrl,
    };

    const [submitErr] = (await to(
      axiosWithAuth(
        `${apiUrl}cohortconfiguration/cloneSolution/create`, // Use the cloneSolution API endpoint
        requestData,
        "post"
      )
    )) as any;

    // Handle the response from the API
    if (submitErr) {
      const { data: { message = null } = {} } = submitErr.response;
      setToastMessage(message);
      setToastOpen(true);
    } else {
      handleCloneClose(); // Close the modal on success
      setCloneFormData({
        cloneFromCohortId: "",
        cloneFromQuizId: "",
        cloneToCohortId: "",
        cloneToQuizId: "",
        teachUrl: "",
      });
      setCloneFormErrors({
        cloneFromCohortId: false,
        cloneFromQuizId: false,
        cloneToCohortId: false,
        cloneToQuizId: false,
        teachUrl: false,
      });

      // Refresh the cohorts data if necessary
      setLoading(true);
      const [fetchErr, response] = await to(
        axiosWithAuth(
          `${apiUrl}cohortconfiguration/cohorts`,
          {
            params: { page: page + 1, limit: rowsPerPage },
          },
          "get"
        )
      );

      if (!fetchErr) {
        setCohorts(response.data.data);
      }
      setLoading(false);
    }

    setIsSubmitting(false); // Reset submitting state
  };

  if (loading) {
    return (
      <Box mt={4} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box mt={4}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  const getCustomFilterOperators = () =>
    getGridStringOperators().filter(
      (operator) => operator.value === "contains" || operator.value === "equals"
    );

  const columns: GridColDef[] = [
    {
      field: "_id",
      headerName: "ID",
      width: 200,
      renderCell: (params) => (
        <Button
          variant="text"
          onClick={() => navigate(`/cohort/${params.value}/quizzes`)}
        >
          {params.value}
        </Button>
      ),
      filterOperators: getCustomFilterOperators(),
    },
    {
      field: "cohortName",
      headerName: "Cohort Name",
      width: 500,
      renderCell: (params) => (
        <Button
          variant="text"
          onClick={() => navigate(`/cohort/${params.row._id}/quizzes`)}
          sx={{ textTransform: "none" }}
        >
          {params.value}
        </Button>
      ),
      filterOperators: getCustomFilterOperators(),
    },
  ];

  return (
    <Box mt={4}>
      <Box display="flex" justifyContent="flex-end" mb={2}>
        {/* <Button onClick={handleCloneOpen} variant="outlined" color="primary" sx={{ mr: 2 }}>
          Clone Solution
        </Button>
        <Button
          variant="outlined"
          color="primary"
          sx={{ mr: 2 }}
          onClick={() => navigate("/masterSolution")}
        >
          List of Master Solutions
        </Button> */}
        <Button onClick={handleOpen} variant="outlined">
          Add New Configuration
        </Button>
      </Box>


      {/* <Box justifyContent={"flex-end"} display={"flex"}>
        <Button onClick={handleOpen} variant="outlined">
          Add New Configuration
        </Button>
      </Box> */}
      <Typography variant="h4" gutterBottom>
        List of Cohorts
      </Typography>

      <DataGrid
        rows={cohorts}
        columns={columns}
        paginationMode="server"
        rowCount={totalCount}
        paginationModel={{
          page: page,
          pageSize: rowsPerPage,
        }}
        onPaginationModelChange={(model) => {
          setPage(model.page);
          setRowsPerPage(model.pageSize);
        }}
        pageSizeOptions={[5, 10, 20, 50, 100]}
        disableRowSelectionOnClick
        loading={loading}
        getRowId={(row) => row._id}
        rowHeight={60}
        filterMode="server"
        // slots={{
        //   columnMenu: CustomColumnMenu,
        // }}
        onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
        onFilterModelChange={(filter) => debounceFetchCohorts(filter)}
      />

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Typography
            id="modal-modal-title"
            variant="h6"
            display={"flex"}
            justifyContent={"space-between"}
          >
            Add New Cohort Configuration
            <IconButton aria-label="close" onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Typography>
          {/* <TextField
            label="Cohort ID"
            name="cohortId"
            value={formData.cohortId}
            onChange={handleFormChange}
            fullWidth
            margin="normal"
            error={formErrors.cohortId}
            helperText={formErrors.cohortId && "Cohort ID is required"}
          /> */}
          <TextField
            label="Quiz ID"
            name="quizId"
            value={formData.quizId}
            onChange={handleFormChange}
            fullWidth
            margin="normal"
            error={formErrors.quizId}
            helperText={formErrors.quizId && "Quiz ID is required"}
          />
          {/* <TextField
            label="Teach URL"
            name="teachUrl"
            value={formData.teachUrl}
            onChange={handleFormChange}
            fullWidth
            margin="normal"
            error={formErrors.teachUrl}
            helperText={formErrors.teachUrl && "Teach URL is required"}
          /> */}
          <FormControl fullWidth margin="normal" error={formErrors.categoryId}>
            <InputLabel>Submission Type</InputLabel>
            <Select
              name="categoryId"
              value={formData.categoryId}
              onChange={handleSelectChange}
              label="Submission Type"
            >
              {categories.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.name}
                </MenuItem>
              ))}
            </Select>
            {formErrors.categoryId && (
              <FormHelperText>Category ID is required</FormHelperText>
            )}
          </FormControl>
          {/* <TextField
            label="Problem Statement"
            name="problemStatement"
            value={formData.problemStatement}
            onChange={handleFormChange}
            fullWidth
            margin="normal"
            multiline
            rows={20}
            error={formErrors.problemStatement}
            helperText={formErrors.problemStatement && "Problem Statement is required"}
          /> */}
          <Box mt={2}>
            <InputLabel sx={{ mb: 1 }}>Problem Statement</InputLabel>
            <FroalaEditorComponent
              value={formData.problemStatement}
              onChange={handleFroalaChange}
              placeholder="Enter the problem statement..."
              height={200}
            />
            {formErrors.problemStatement && (
              <FormHelperText sx={{ color: "#d32f2f" }}>
                Problem Statement is required
              </FormHelperText>
            )}
          </Box>
          <Box justifyContent={"flex-end"} display={"flex"}>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleFormSubmit}
              disabled={isSubmitting} // Disable button when submitting
              sx={{ mt: 2 }}
            >
              {isSubmitting ? "Saving..." : "Submit"} {/* Change button text */}
            </Button>
          </Box>
        </Box>
      </Modal>


      {/* <Modal
  open={cloneOpen}
  onClose={handleCloneClose}
  aria-labelledby="clone-modal-title"
  aria-describedby="clone-modal-description"
>
  <Box sx={style}>
    <Typography
      id="clone-modal-title"
      variant="h6"
      display={"flex"}
      justifyContent={"space-between"}
    >
      Clone Solution for new cohort
      <IconButton aria-label="close" onClick={handleCloneClose}>
        <CloseIcon />
      </IconButton>
    </Typography>
    <TextField
      label="Clone From Cohort ID"
      name="cloneFromCohortId"
      value={cloneFormData.cloneFromCohortId}
      onChange={handleCloneFormChange}
      fullWidth
      margin="normal"
      error={cloneFormErrors.cloneFromCohortId}
      helperText={cloneFormErrors.cloneFromCohortId && "Cohort ID is required"}
    />
    <TextField
      label="Clone From Quiz ID"
      name="cloneFromQuizId"
      value={cloneFormData.cloneFromQuizId}
      onChange={handleCloneFormChange}
      fullWidth
      margin="normal"
      error={cloneFormErrors.cloneFromQuizId}
      helperText={cloneFormErrors.cloneFromQuizId && "Quiz ID is required"}
    />
    <TextField
      label="Clone To Cohort ID"
      name="cloneToCohortId"
      value={cloneFormData.cloneToCohortId}
      onChange={handleCloneFormChange}
      fullWidth
      margin="normal"
      error={cloneFormErrors.cloneToCohortId}
      helperText={cloneFormErrors.cloneToCohortId && "Cohort ID is required"}
    />
    <TextField
      label="Clone To Quiz ID"
      name="cloneToQuizId"
      value={cloneFormData.cloneToQuizId}
      onChange={handleCloneFormChange}
      fullWidth
      margin="normal"
      error={cloneFormErrors.cloneToQuizId}
      helperText={cloneFormErrors.cloneToQuizId && "Quiz ID is required"}
    />
    <TextField
      label="Teach URL"
      name="teachUrl"
      value={cloneFormData.teachUrl}
      onChange={handleCloneFormChange}
      fullWidth
      margin="normal"
      error={cloneFormErrors.teachUrl}
      helperText={cloneFormErrors.teachUrl && "Teach URL is required"}
    />
    <Box justifyContent={"flex-end"} display={"flex"}>
      <Button
        variant="outlined"
        color="primary"
        onClick={handleCloneFormSubmit}
        sx={{ mt: 2 }}
      >
        Submit
      </Button>
    </Box>
  </Box>
</Modal> */}
      <Modal
        open={cloneOpen}
        onClose={handleCloneClose}
        aria-labelledby="clone-modal-title"
        aria-describedby="clone-modal-description"
      >
        <Box sx={style}>
          <Typography
            id="clone-modal-title"
            variant="h6"
            display="flex"
            justifyContent="space-between"
          >
            Clone Autograder Solution
            <IconButton aria-label="close" onClick={handleCloneClose}>
              <CloseIcon />
            </IconButton>
          </Typography>

          {/* Clone From Section */}
          <Typography variant="subtitle1" sx={{ mt: 2 }}>
            Clone From
          </Typography>
          <Box display="flex" gap={2}>
            <TextField
              label="Cohort ID"
              name="cloneFromCohortId"
              value={cloneFormData.cloneFromCohortId}
              onChange={handleCloneFormChange}
              fullWidth
              margin="normal"
              error={cloneFormErrors.cloneFromCohortId}
              helperText={cloneFormErrors.cloneFromCohortId && "Cohort ID is required"}
            />
            <TextField
              label="Quiz ID"
              name="cloneFromQuizId"
              value={cloneFormData.cloneFromQuizId}
              onChange={handleCloneFormChange}
              fullWidth
              margin="normal"
              error={cloneFormErrors.cloneFromQuizId}
              helperText={cloneFormErrors.cloneFromQuizId && "Quiz ID is required"}
            />
          </Box>

          {/* Clone To Section */}
          <Typography variant="subtitle1" sx={{ mt: 2 }}>
            Clone To
          </Typography>
          <Box display="flex" gap={2}>
            <TextField
              label="Cohort ID"
              name="cloneToCohortId"
              value={cloneFormData.cloneToCohortId}
              onChange={handleCloneFormChange}
              fullWidth
              margin="normal"
              error={cloneFormErrors.cloneToCohortId}
              helperText={cloneFormErrors.cloneToCohortId && "Cohort ID is required"}
            />
            <TextField
              label="Quiz ID"
              name="cloneToQuizId"
              value={cloneFormData.cloneToQuizId}
              onChange={handleCloneFormChange}
              fullWidth
              margin="normal"
              error={cloneFormErrors.cloneToQuizId}
              helperText={cloneFormErrors.cloneToQuizId && "Quiz ID is required"}
            />
          </Box>

          {/* Teach URL Section */}
          <TextField
            label="Teach URL"
            name="teachUrl"
            value={cloneFormData.teachUrl}
            onChange={handleCloneFormChange}
            fullWidth
            margin="normal"
            error={cloneFormErrors.teachUrl}
            helperText={cloneFormErrors.teachUrl && "Teach URL is required"}
          />

          <Box justifyContent="flex-end" display="flex">
            <Button
              variant="outlined"
              color="primary"
              onClick={handleCloneFormSubmit}
              disabled={isSubmitting}
              sx={{ mt: 2 }}
            >

              {isSubmitting ? "Saving..." : "Submit"}
            </Button>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={toastOpen}
        autoHideDuration={6000}
        onClose={handleToastClose}
      >
        <Alert
          onClose={handleToastClose}
          severity="error"
          sx={{ width: "100%" }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
export default Cohorts;
